import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '../../layout/icons';
import { stripEdges, stripPageTarget, stripRevealTarget } from '../filmstrip/overflow';
import { useSubmissionStore } from './store';
import { itemLabel, itemPosition, navigatorMeta, submissionItems } from './submissions';
import '../../styles/submissions.css';

// width of the fade over each end of the strip, as .hw-nav__more
const MORE_WIDTH = 44;

/** The chip strip's hidden ends, and the scroll that keeps the shown chip in view. */
function useChipStrip(shown: number, revision: string) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = stripEdges(el.scrollLeft, el.clientWidth, el.scrollWidth);
    setEdges((current) => (current.start === next.start && current.end === next.end ? current : next));
  }, []);

  const page = useCallback((direction: number) => {
    const el = ref.current;
    if (!el) return;
    el.scrollLeft = stripPageTarget(el.scrollLeft, el.clientWidth, el.scrollWidth, direction);
    measure();
  }, [measure]);

  // before paint, so the next student never shows the last one's scroll position
  useLayoutEffect(() => {
    const el = ref.current;
    const chip = el ? (el.children[shown] as HTMLElement | undefined) : undefined;
    if (el && chip) el.scrollLeft = stripRevealTarget({ left: chip.offsetLeft, right: chip.offsetLeft + chip.offsetWidth }, el.scrollLeft, el.clientWidth, el.scrollWidth, MORE_WIDTH);
    measure();
  }, [shown, revision, measure]);

  useEffect(() => {
    window.addEventListener('resize', measure, false);
    return () => window.removeEventListener('resize', measure, false);
  }, [measure]);

  return { ref, edges, measure, page };
}

// The bar over the board while a class's submissions are on show: whose work, its files, next/prev
export function SubmissionNavigator({ onClose }: { onClose: () => void }) {
  const viewer = useSubmissionStore((state) => state.viewer);
  const step = useSubmissionStore((state) => state.step);
  const selectItem = useSubmissionStore((state) => state.selectItem);
  const showMarks = useSubmissionStore((state) => state.showMarks);
  const current = viewer ? viewer.order[viewer.index] : null;
  const items = current ? submissionItems(current) : [];
  const shown = viewer ? Math.min(viewer.item, items.length - 1) : 0;
  const strip = useChipStrip(shown, current ? current.id : '');
  if (!viewer || !current) return null;
  const meta = navigatorMeta(viewer, showMarks);

  return (
    <div className="hw-nav" role="group" aria-label="Bài nộp của học sinh">
      <button type="button" className="hw-nav__step" aria-label="Học sinh trước" disabled={viewer.index <= 0} onClick={() => step(-1)}><ChevronLeftIcon size={22} /></button>
      <div className="hw-nav__who" aria-live="polite">
        <strong title={current.studentName}>{current.studentName}</strong>
        <span title={meta}>{meta}</span>
      </div>
      {items.length > 1 ? (
        <>
          <span className="hw-nav__count">{itemPosition(items, shown)}</span>
          <div className="hw-nav__strip">
            <div className="hw-nav__items" ref={strip.ref} onScroll={strip.measure} role="group" aria-label="Nội dung bài nộp">
              {items.map((_item, index) => (
                <button type="button" key={index} className={index === shown ? 'hw-nav__item is-active' : 'hw-nav__item'} aria-pressed={index === shown} onClick={() => selectItem(index)}>{itemLabel(items, index)}</button>
              ))}
            </div>
            {/* touch only: every chip is already in the tab order, and focus scrolls it into view */}
            {strip.edges.start ? <button type="button" className="hw-nav__more hw-nav__more--start" tabIndex={-1} aria-hidden={true} onClick={() => strip.page(-1)}><span className="hw-nav__more-glyph"><ChevronLeftIcon size={16} /></span></button> : null}
            {strip.edges.end ? <button type="button" className="hw-nav__more hw-nav__more--end" tabIndex={-1} aria-hidden={true} onClick={() => strip.page(1)}><span className="hw-nav__more-glyph"><ChevronRightIcon size={16} /></span></button> : null}
          </div>
        </>
      ) : null}
      <button type="button" className="hw-nav__step" aria-label="Học sinh tiếp theo" disabled={viewer.index >= viewer.order.length - 1} onClick={() => step(1)}><ChevronRightIcon size={22} /></button>
      <button type="button" className="hw-nav__close" aria-label="Đóng bài nộp, quay lại bài giảng" onClick={onClose}><CloseIcon size={20} /></button>
    </div>
  );
}
